import { MycronLogo } from "../../assets/icons";
import { TenantBrand, TenantColors, TenantKey } from "./types";


/**
 * Tenant used when no tenant can be resolved (unknown host / no param / no JWT claim).
 * Keeps the app looking exactly like today when resolution falls through.
 */
export const DEFAULT_TENANT: TenantKey = "xepp";

/**
 * Single fallback brand-color pair, used by every tenant until its real colors
 * are fetched from the backend branding endpoint, or if that fetch fails.
 * Colors are never hardcoded per tenant (see TenantProvider) — matches the
 * --app-primary / --app-secondary defaults in src/color-scheme.scss.
 */
export const DEFAULT_COLORS: TenantColors = {
  primary: "rgb(237, 50, 55)",
  secondary: "rgb(44, 39, 104)",
};

/**
 * Static brand registry — single source of truth for tenant branding.
 * Adding a tenant: add its key to TenantKey (types.ts) and an entry here. A real
 * tenant's logo and colors are NOT part of this registry — they come from
 * TenantProvider's branding fetch (no logo fallback: none configured, none shown);
 * only a brand with no backend branding record spells them out here.
 */
export const tenantRegistry: Record<TenantKey, TenantBrand> = {
  // Default/platform tenant — used when no tenant resolves (unknown host / no JWT).
  xepp: {
    key: "xepp",
    name: "Xepp",
    // poweredBy: { name: "Mycron", logo: MycronLogo },
  },
  ekedc: {
    key: "ekedc",
    name: "Ekedp Power App",
    // Only ekedc has an app-info graphic, hardcoded until the backend supports it.
    appInfoText: "/branding/ekedc/app-info-text.svg",
    poweredBy: { name: "Mycron", logo: MycronLogo }, // TODO: per-tenant partner
    legalContact: {
      address: "24/25 Marina Rd Lagos - Island. Lagos",
      email: "customercare@ekedp.com",
      complaintEmail: "info@mycron.co",
      phones: ["0708 067 1170", "0700 123 5666"],
    },
  },
  aedc: {
    key: "aedc",
    name: "AEDC",
    // poweredBy: { name: "Mycron", logo: MycronLogo }, // TODO: per-tenant partner
  },
  // Not a utility — the BPS admin portal's own brand (Beacon Power Services).
  bps: {
    key: "bps",
    name: "Beacon Power Services",
    logo: "/branding/bps/logo.png", // TODO: real BPS logo (currently the Xepp mark)
    logoWithText: "/branding/bps/logo-with-text.png", // TODO: real BPS wordmark
    // Read off the yansa logo — TODO: confirm the exact brand hex with design.
    colors: {
      primary: "#F2942C",
      secondary: "#17171A",
    },
  },
  lepp: {
    key: "lepp",
    name: "Lepp",
    // poweredBy: { name: "Mycron", logo: MycronLogo }, // TODO: per-tenant partner
  },
  solar: {
    key: "solar",
    name: "Solar",
  },
};

/** Type guard: is the given string a known tenant key? */
function isKnownTenant(key: string | null | undefined): key is TenantKey {
  return !!key && Object.prototype.hasOwnProperty.call(tenantRegistry, key);
}

/**
 * Alternate spellings that map onto a canonical tenant key, so a tenant can be
 * referred to by more than one name (subdomain, ?tenant= param, or JWT code).
 */
const tenantAliases: Record<string, TenantKey> = {
  ekedp: "ekedc",
};

/**
 * Canonical tenant key for a raw string — a known key as-is, or an alias
 * (e.g. "ekedp" -> "ekedc"). Returns undefined when nothing matches.
 */
function canonicalTenantKey(key: string | null | undefined): TenantKey | undefined {
  if (!key) return undefined;
  if (isKnownTenant(key)) return key;
  return tenantAliases[key.toLowerCase()];
}

/**
 * Always returns a valid brand. Unknown / missing key falls back to DEFAULT_TENANT,
 * so consumers never have to null-check.
 */
export function getBrand(key: string | null | undefined): TenantBrand {
  return tenantRegistry[canonicalTenantKey(key) ?? DEFAULT_TENANT];
}
